import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { RootStore } from 'core/stores/root-store';
import { FormControl, FormGroup } from 'shared/libs/form';
import { AsyncState } from 'shared/libs/state';
import { HttpClient } from 'shared/libs/http';
import { UserApiService } from 'user';
import { VerifyAccountDialogStore } from 'feature-auth/verify-account-dialog/verify-account-dialog.store';
import {
   CandidateLoginPayload,
   CandidateLoginResponse,
   CandidateLoginSuccessResponse,
   CandidateLoginVerificationResponse,
} from './candidate-login.types';

type CandidateLoginForm = {
   login: FormControl<string>;
   password: FormControl<string>;
};

type CandidateLoginSubmitResult =
   | {
        success: true;
     }
   | {
        success: false;
        status?: number;
        message?: string;
     };

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^\+\d{1,3}\d{6,14}$/;

export class CandidateLoginStore {
   readonly loginState = new AsyncState<CandidateLoginResponse>();
   readonly verifyAccountDialogStore: VerifyAccountDialogStore;
   showPassword = false;
   captchaToken: string | null = null;
   captchaError = '';
   form!: FormGroup<CandidateLoginForm>;

   constructor(
      readonly rootStore: RootStore,
      private readonly t: (key: string) => string,
      private readonly language: string
   ) {
      this.form = new FormGroup({
         login: new FormControl<string>('', [
            (value) => {
               const loginValue = value.trim();

               if (!loginValue) {
                  return this.t('jobseekerLogin.loginRequired');
               }

               if (!emailPattern.test(loginValue) && !phonePattern.test(loginValue)) {
                  return this.t('jobseekerLogin.invalidLogin');
               }

               return '';
            },
         ]),
         password: new FormControl<string>('', [
            (value) => {
               if (!value.trim()) {
                  return this.t('jobseekerLogin.passwordRequired');
               }
               return '';
            },
         ]),
      });
      this.verifyAccountDialogStore = new VerifyAccountDialogStore(rootStore, language);

      makeObservable(this, {
         showPassword: observable,
         captchaToken: observable,
         captchaError: observable,
         submitting: computed,
         hasActiveSession: computed,
         setCaptchaToken: action.bound,
         setCaptchaError: action.bound,
         togglePasswordVisibility: action.bound,
      });
   }

   get submitting(): boolean {
      return this.loginState.status === 'pending';
   }

   get hasActiveSession(): boolean {
      const tokenClient = Cookies.get('tokenClient');
      const userType = Cookies.get('user_type');

      return Boolean(tokenClient && (userType === 'recruiter' || userType === 'candidate'));
   }

   setCaptchaToken(token: string | null): void {
      this.captchaToken = token;

      if (token) {
         this.captchaError = '';
      }
   }

   setCaptchaError(message: string): void {
      this.captchaError = message;
   }

   togglePasswordVisibility(): void {
      this.showPassword = !this.showPassword;
   }

   private validateForm(): boolean {
      this.form.controls.login.markAsTouched();
      this.form.controls.password.markAsTouched();
      this.form.validate();

      if (!this.captchaToken) {
         this.captchaError = this.t('jobseekerLogin.captchaRequired');
      } else {
         this.captchaError = '';
      }

      return this.form.valid && !!this.captchaToken;
   }

   private trackLoginError(message: string): void {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
         event: 'website_error',
         error_type: 'login error',
         error_message: message,
      });
   }

   private setUserCookies(user: CandidateLoginSuccessResponse['response']['user']): void {
      Cookies.set('lname', user.last_name);
      Cookies.set('user_id', String(user.user_id));
      Cookies.set('fname', user.first_name);
      Cookies.set('tokenClient', user.token);
      Cookies.set('user_type', user.user_type);
      Cookies.set('contactNumber', user.contact || '');
      Cookies.set('emailAddress', user.email_address);
   }

   private isSuccessResponse(response: CandidateLoginResponse): response is CandidateLoginSuccessResponse {
      if (response.status !== 200) {
         return false;
      }

      if (typeof response.response !== 'object' || response.response === null) {
         return false;
      }

      if (Array.isArray(response.response)) {
         return false;
      }

      if (!('user' in response.response)) {
         return false;
      }

      return Boolean(response.response.user?.token);
   }

   private isVerificationResponse(
      response: CandidateLoginResponse
   ): response is CandidateLoginVerificationResponse {
      return response.status === 200 && 'requires_verification' in response && response.requires_verification;
   }

   async submit(
      {
         onSuccess,
         onVerificationRequired,
         onError,
      }: {
         onSuccess?: (
            resp: CandidateLoginSuccessResponse,
            user: CandidateLoginSuccessResponse['response']['user']
         ) => void | Promise<void>;
         onVerificationRequired?: (
            resp: CandidateLoginVerificationResponse
         ) => void | Promise<void>;
         onError?: (message: string, status?: number) => void | Promise<void>;
      } = {}
   ): Promise<CandidateLoginSubmitResult> {
      const valid = this.validateForm();
      if (!valid) {
         return { success: false };
      }

      const payload: CandidateLoginPayload = {
         login: this.form.controls.login.value.trim(),
         password: this.form.controls.password.value,
         language: this.language,
      };

      let result: CandidateLoginSubmitResult = { success: false };

      await this.loginState.run(
         async () => HttpClient.post<CandidateLoginResponse>('/users/login', payload),
         {
            onSuccess: async (response) => {
               if (this.isSuccessResponse(response)) {
                  const user = response.response.user;

                  if (user.user_type !== 'candidate') {
                     const message = this.t('jobseekerLogin.wrongCredentials');
                     this.trackLoginError(message);
                     result = { success: false, status: 401, message };
                     await onError?.(message, 401);
                     return;
                  }

                  await UserApiService.setContext({
                     user_id: user.user_id,
                     user_type: user.user_type,
                     lang: this.language,
                  });

                  runInAction(() => {
                     this.rootStore.userStore.setUser(user);
                  });

                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                     event: 'login',
                     user_id: user.user_id,
                     authentication_method: 'email',
                  });

                  this.setUserCookies(user);
                  result = { success: true };
                  await onSuccess?.(response, user);
                  return;
               }

               if (this.isVerificationResponse(response)) {
                  result = { success: false, status: response.status, message: response.message };
                  await onVerificationRequired?.(response);
                  return;
               }

               this.trackLoginError(response.message);
               result = { success: false, status: response.status, message: response.message };
               await onError?.(response.message, response.status);
            },
            onError: async (error) => {
               this.trackLoginError(error.message);
               result = { success: false, message: error.message };
               await onError?.(error.message, error.status);
            },
         }
      );

      return result;
   }
}
